import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import ContactAvatar from "@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar";
import RowStack from "@common-components/common/row-stack/RowStack";
import { Box, IconButton, Skeleton, Tooltip } from "@mui/material";
import { extractPhoneNumber, getWaContactDisplayLabel, selectWhatsappContactById } from "@common-reducers/WhatsAppSelectors";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CellProps } from "../cell-interface";
import { PhoneDisplay } from "../phone-number-cell/phone-display";
import { DBTasksThunks, isTempId, taskColumnSelectByQuery, tasksSelectByQuery } from "@common-reducers/DBServiceThunks";
import AddContactsToCampaignDialog from "@common-components/tasks/new-task-row/add-contacts-to-campaign-dialog";
import { SelectedContact, getContactSerializedId } from "@extension/context/content/components/shared";
import { MdOutlineContentCopy } from "react-icons/md";
import { buildContactCustomData, findNameColumns, selectedContactDisplayName, type CustomData } from "@common-components/tasks/new-task-row/contact-task-helpers";

const COPIED_HINT_MS = 1500;

export const WhatsappIdCell = ({
    cellValue,
    taskId,
    columnId,
    withoutPlus = false,
}: CellProps) => {
    const dispatch = useAppDispatch();
    const contact = useAppSelector((state) => selectWhatsappContactById(state, cellValue));
    const task = useAppSelector((state) => tasksSelectByQuery(state.DBTasksReducer, { _id: taskId })?.[0]);
    const boardColumns = useAppSelector((state) =>
        taskColumnSelectByQuery(state.DBTaskColumnReducer, { boardId: task?.boardId, deleted: false })
    );

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => {
            if (copiedTimeoutRef.current) {
                clearTimeout(copiedTimeoutRef.current);
            }
        };
    }, []);

    const isGroup = chatKindOf(cellValue) === 'group';
    const isChannel = chatKindOf(cellValue) === 'channel';
    const isRawId = isGroup || isChannel;

    const phoneNumber = useMemo(() => extractPhoneNumber(cellValue, withoutPlus), [cellValue, withoutPlus]);
    const rawId = useMemo(() => (cellValue ?? '').split('@')[0], [cellValue]);
    const nameColumns = useMemo(() => findNameColumns(boardColumns), [boardColumns]);

    // Match the ID format the contact list uses, so the row highlights correctly
    // in the picker. Falls back to the raw cellValue when the contact isn't in
    // cache (cell value can be a PN-format ID while the cached entry is LID).
    const initialSelected = useMemo<SelectedContact[] | undefined>(() => {
        if (!cellValue) return undefined;
        const label = contact ? getWaContactDisplayLabel(contact) : phoneNumber;
        return [{
            id: (contact && getContactSerializedId(contact)) || cellValue,
            name: label,
            displayName: label,
            imgUrl: (contact as any)?.imgUrl,
        }];
    }, [cellValue, contact, phoneNumber]);

    const handleClick = () => {
        if (!isTempId(taskId ?? null)) setIsDialogOpen(true);
    };

    // Single atomic patch keeps cellValue, text, and first/last name in lockstep.
    // Splitting it into two dispatches caused a race: the second patch's
    // customData spread (built from the pre-update task snapshot) overwrote the
    // first's whatsapp-id back to the old value.
    const handleConfirm = useCallback((picked: SelectedContact[]) => {
        const next = picked[0];
        const newValue = next?.id ?? '';
        if (!taskId || newValue === (cellValue ?? '')) return;

        // Clearing the cell leaves the row's display name alone so the user can
        // rename freely without losing context.
        if (!newValue) {
            // Declare-then-assign (mirrors buildContactCustomData) so the value
            // is inferred as CustomData rather than the wider Record type an
            // inline computed-key literal produces.
            const customData: CustomData = { ...(task?.customData || {}) };
            customData[columnId!] = { value: '' };
            dispatch(DBTasksThunks.patch({ entity: { _id: taskId, customData } }));
            return;
        }

        const displayName = selectedContactDisplayName(next);
        const customData = buildContactCustomData({
            base: task?.customData,
            whatsappIdColumnId: columnId!,
            contactId: newValue,
            displayName,
            ...nameColumns,
        });
        dispatch(DBTasksThunks.patch({
            entity: { _id: taskId, text: displayName, customData },
        }));
    }, [dispatch, taskId, columnId, cellValue, task?.customData, nameColumns]);

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        const text = isRawId ? rawId : phoneNumber;
        if (!text) return;
        navigator.clipboard?.writeText(text);
        setCopied(true);
        copiedTimeoutRef.current = setTimeout(() => setCopied(false), COPIED_HINT_MS);
    };

    const renderValue = () => {
        if (isRawId) {
            return (
                <Box
                    sx={{
                        fontSize: 12,
                        color: 'var(--tasks-text-secondary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 160,
                    }}
                    title={rawId}
                >
                    {rawId}
                </Box>
            );
        }
        return <PhoneDisplay phone={phoneNumber} />;
    };

    const renderCellContent = () => {
        if (!cellValue) {
            return (
                <Box sx={{ fontSize: 15, width: 1, textAlign: 'center' }}>
                    Click to add a contact
                </Box>
            );
        }
        return (
            <>
                <ContactAvatar sx={{ mr: 1 }} contactId={cellValue} size={24} textSize={12} />
                {renderValue()}
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                    <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, p: 0.25 }}>
                        <MdOutlineContentCopy size={14} color="var(--tasks-text-secondary)" />
                    </IconButton>
                </Tooltip>
            </>
        );
    };

    return (
        <>
            <RowStack
                sx={{ px: 1, overflow: 'hidden', cursor: 'pointer' }}
                flex={1}
                justifyContent="center"
                onClick={handleClick}
                id={`wid_${taskId}_${columnId}`}
            >
                {isTempId(taskId ?? null) ? <Box sx={{ width: 1, height: 1 }}><Skeleton /></Box> : renderCellContent()}
            </RowStack>
            <AddContactsToCampaignDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                onConfirm={handleConfirm}
                initialSelected={initialSelected}
                isMultipleSelection={false}
            />
        </>
    );
};
